import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { Link } from '@abyss/web/ui/Link';
import find from 'lodash/find';
import { useShallow } from 'zustand/react/shallow';

import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useLagoon } from '../../hooks/useLagoon';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { getLob } from '../../utils/providerSearch.utils';
import {
  getCurrentMember,
  isDsnpMember,
  isIndividualMNRMember,
} from '../../utils/user.utils';
import { CareCategories, Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { ContentWrapper } from '../ContentWrapper';
import { DisclaimerContainer } from '../ProviderDetailTabs/ProviderDetailTabs.styled';
import {
  HealthGradesStyled,
  ProviderDirectoryText,
  ProviderDirectoryWrapper,
  StyledMarkdown,
} from './DisclaimersContainer.styled';
import { HealthGradesDisclaimers } from './HealthGradesDisclaimers';

interface Props {
  isGuestUser?: boolean;
  showPremiumCareDisclaimer?: boolean;
  ratingSection?: boolean;
  selectedChipValue?: string;
  isViewAllDisclaimer?: boolean;
  isLandingPage?: boolean;
  search?: string;
  sectionType?: string;
}

export const MNRDisclaimersContainer = ({
  isGuestUser = false,
  ratingSection = false,
  selectedChipValue,
  isViewAllDisclaimer,
  isLandingPage,
  sectionType,
  search,
}: Props) => {
  const lagoonData = useLagoon('ui-messaging')();
  const isWidget = useStore(StoreKeys.IS_WIDGET);

  const modalDisclaimerFull = find(lagoonData, {
    key: ConstantsLagoon.HEALTHGRADES.MODAL_DISCLAIMER_FULL,
  });

  const dentalMnrDisclaimer = find(lagoonData, {
    key: ConstantsLagoon.MNR_DISCLAIMER.DENTAL_MNR_DISCLAIMER,
  });

  const medicalFacilityMnrDisclaimer = find(lagoonData, {
    key: ConstantsLagoon.MNR_DISCLAIMER.MEDICAL_FACILITY_MNR_DISCLAIMER,
  });

  const visionMnrDisclaimer = find(lagoonData, {
    key: ConstantsLagoon.MNR_DISCLAIMER.VISION_MNR_DISCLAIMER,
  });

  const healthGradeMnrDisclaimerContent = find(lagoonData, {
    key: ConstantsLagoon.MNR_DISCLAIMER.HEALTH_GRADES_MNR_DISCLAIMER_CONTENT,
  });

  const directoryMnrDisclaimer = find(lagoonData, {
    key: ConstantsLagoon.MNR_DISCLAIMER.DIRECTORY_MNR_DISCLAIMER,
  });

  const guestGeneralDisclaimer = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.GUEST_GENERAL_DISCLAIMER,
  });

  const [enableMapViewHideOption] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_HIDE_OPTION,
  ]);

  const providerDirectoryDisclaimerData = find(lagoonData, {
    key: ConstantsLagoon.MNR_DISCLAIMER.PROVIDER_DIRECTORY_PDF_LINK,
  });

  const [enableProviderDirectoryPdfLink] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PROVIDER_DIRECTORY_PDF_LINK,
  ]);

  const currentMember = getCurrentMember();
  const checkDsnpMember = isDsnpMember(currentMember);
  const checkIndividualMnrMember = isIndividualMNRMember(currentMember);
  const isValidProviderDirectoryPdfmember =
    enableProviderDirectoryPdfLink &&
    providerDirectoryDisclaimerData &&
    selectedChipValue === CareCategories.ALL &&
    (checkDsnpMember || checkIndividualMnrMember);

  const { storeLob } = useChipStore(
    useShallow((state: ChipState) => ({
      storeLob: state.lob,
    }))
  );
  const lob = getLob(currentMember?.lineOfBusiness, storeLob);

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { locale } = chipStore;

  const renderMnrDisclaimer = () => {
    switch (selectedChipValue) {
      case CareCategories.DENTAL:
        return dentalMnrDisclaimer?.message;
      case CareCategories.MEDICAL_SPECIALISTS:
      case CareCategories.FACILITIES:
        return medicalFacilityMnrDisclaimer?.message;
      case CareCategories.VISION:
        return visionMnrDisclaimer?.message;
      case CareCategories.ALL:
        return directoryMnrDisclaimer?.message;
      default:
        return '';
    }
  };

  const renderLandingPageDisclaimer = () => {
    switch (selectedChipValue) {
      case CareCategories.ALL:
        return directoryMnrDisclaimer?.message;
      default:
        return '';
    }
  };

  const renderHealthGradesDisclaimer = () => {
    const renderDisclaimers = () => (
      <HealthGradesDisclaimers
        disclaimerContent={healthGradeMnrDisclaimerContent?.message}
        modalContent={modalDisclaimerFull?.message}
      />
    );

    const isFacilitySection = sectionType === Constants.FACILITY;
    const isOnlineProviderSearch =
      search?.toLocaleLowerCase() ===
      Constants.ONLINE_PROVIDERS.toLocaleLowerCase();

    const isSelectedChipValueInCategories = [
      CareCategories.FACILITIES,
      CareCategories.HEALTH_SUPPLIES,
      CareCategories.ALL,
    ].includes(selectedChipValue as CareCategories);

    if (isFacilitySection && isOnlineProviderSearch) return renderDisclaimers();
    if (isFacilitySection || isSelectedChipValueInCategories) return '';

    return renderDisclaimers();
  };

  const returnGuestDisclaimer = (disclaimer?: string) =>
    disclaimer || (isGuestUser ? guestGeneralDisclaimer?.message : '');

  return (
    <DisclaimerContainer cssProps={{ ratingSection, enableMapViewHideOption }}>
      <HealthGradesStyled
        cssProps={{ isWidget }}
        data-auto-testid="standard-disclaimer-content"
        data-testid="standard-disclaimer-content-for-mnr"
      >
        <ContentWrapper isWidget={isWidget}>
          {isValidProviderDirectoryPdfmember ? (
            <ProviderDirectoryWrapper>
              <ProviderDirectoryText>
                {providerDirectoryDisclaimerData?.title}
              </ProviderDirectoryText>
              <Link
                href={`/provider-directory?lob=${lob}&locale=${locale}`}
                isStandardAnchor
                rel="noopener noreferrer"
                size={12}
                target="_blank"
              >
                {providerDirectoryDisclaimerData?.message}
                <IconSymbol
                  color="$interactive"
                  icon="chevron_right"
                  size="18"
                />
              </Link>
            </ProviderDirectoryWrapper>
          ) : null}
          <Layout.Stack alignItems="left" space="0">
            {isViewAllDisclaimer ? (
              <StyledMarkdown data-testid="viewall-mnr-standard-disclaimer">
                {returnGuestDisclaimer(renderMnrDisclaimer())}
              </StyledMarkdown>
            ) : (
              ''
            )}

            {isLandingPage ? (
              <StyledMarkdown data-testid="landing-mnr-standard-disclaimer">
                {returnGuestDisclaimer(renderLandingPageDisclaimer())}
              </StyledMarkdown>
            ) : (
              ''
            )}
            {!isGuestUser ? renderHealthGradesDisclaimer() : null}
          </Layout.Stack>
        </ContentWrapper>
      </HealthGradesStyled>
    </DisclaimerContainer>
  );
};
